import type {
  AssessmentBuyerQuery,
  AssessmentCustomerQuestion,
  AssessmentEffort,
  AssessmentSignalStrength,
  OwnerNamedCompetitor,
  SiteAgainAssessmentReportV1,
} from "@siteagain/shared-types";

/**
 * The owner's audit: what a business owner reads after onboarding. It answers
 * three questions (can customers find me, who do they find instead, what do I
 * do next) from the report's public evidence. Everything here is derived from
 * what the report observed; a block with no evidence says "not checked", never
 * a default.
 *
 * Pure and dependency-free so the derivation is testable without a browser.
 */

export type FactLabel = "good" | "needs_work" | "missing" | "not_checked";

export type OwnerFact =
  | { key: "name"; label: FactLabel; position: number | null; term: string | null }
  | { key: "offer"; label: FactLabel; found: number; tested: number }
  | { key: "ai"; label: FactLabel; mentioned: number; asked: number; assistants: number }
  | { key: "links"; label: FactLabel; referringDomains: number | null };

/** A directional bar for "recommended by other sites", not an industry benchmark. */
const WELL_LINKED_DOMAINS = 30;
/** Above this a mobile homepage load is slow enough that visitors leave. */
const SLOW_SECONDS = 2.5;
/** Google shows roughly sixty characters of a title before cutting it. */
const TITLE_CUTOFF = 60;

export function ownerFacts(report: SiteAgainAssessmentReportV1): OwnerFact[] {
  const evidence = report.buyerEvidence;
  const brand = evidence?.brandQuery ?? null;
  const queries = evidence?.queries ?? [];
  const prompts = evidence?.aiPrompts ?? [];
  const found = queries.filter((query) => query.sitePosition !== null).length;
  const mentioned = prompts.filter((prompt) => prompt.answers.some((answer) => answer.siteMentioned)).length;
  const assistants = new Set(prompts.flatMap((prompt) => prompt.answers.map((answer) => answer.vendor))).size;
  const referring = evidence?.backlinkSummary?.referringDomains ?? null;

  return [
    {
      key: "name",
      label: brand === null ? "not_checked" : brand.sitePosition === 1 ? "good" : brand.sitePosition === null ? "missing" : "needs_work",
      position: brand?.sitePosition ?? null,
      term: brand?.query ?? null,
    },
    { key: "offer", label: share(found, queries.length), found, tested: queries.length },
    { key: "ai", label: share(mentioned, prompts.length), mentioned, asked: prompts.length, assistants },
    {
      key: "links",
      label: referring === null ? "not_checked" : referring === 0 ? "missing" : referring < WELL_LINKED_DOMAINS ? "needs_work" : "good",
      referringDomains: referring,
    },
  ];
}

function share(hits: number, total: number): FactLabel {
  if (total === 0) return "not_checked";
  if (hits === 0) return "missing";
  return hits * 2 >= total ? "good" : "needs_work";
}

// ── Verdict ────────────────────────────────────────────────────────────────

export type BuildState = "solid" | "fast_with_issues" | "slow" | "unreadable" | "unknown";
export type ReachState = "found" | "google_not_ai" | "partly" | "name_only" | "invisible" | "unknown";

/** The two halves of the one-line verdict: how the site is built, and who finds it. */
export function ownerVerdict(report: SiteAgainAssessmentReportV1): { build: BuildState; reach: ReachState } {
  const checks = holdingBackChecks(report);
  const failed = (key: HoldingBackCheck["key"]) => checks.some((check) => check.key === key && check.status === "fail");
  const speed = checks.find((check) => check.key === "speed");
  const build: BuildState = failed("readable")
    ? "unreadable"
    : speed?.status === "fail"
      ? "slow"
      : speed?.status === "pass"
        ? checks.some((check) => check.status === "fail") ? "fast_with_issues" : "solid"
        : "unknown";

  const facts = ownerFacts(report);
  const label = (key: OwnerFact["key"]) => facts.find((fact) => fact.key === key)?.label ?? "not_checked";
  const offer = label("offer");
  const ai = label("ai");
  const name = label("name");
  const reach: ReachState = offer === "not_checked"
    ? "unknown"
    : offer === "good"
      ? ai === "missing" ? "google_not_ai" : "found"
      : offer === "needs_work"
        ? "partly"
        : name === "missing" ? "invisible" : "name_only";
  return { build, reach };
}

// ── Who customers find instead ─────────────────────────────────────────────

export type ResultKind = "business" | "forum" | "directory" | "builder" | "government" | "media" | "social" | "reference";

const KIND_BY_HOST: Array<[RegExp, ResultKind]> = [
  [/(^|\.)(reddit\.com|quora\.com|stackexchange\.com|stackoverflow\.com|commentcamarche\.net|developpez\.net|forum-entreprise\.com|webrankinfo\.com)$/u, "forum"],
  [/^(forum|forums|community|communaute)\./u, "forum"],
  [/(^|\.)(pagesjaunes\.fr|yelp\.[a-z.]+|tripadvisor\.[a-z.]+|trustpilot\.com|yellowpages\.com|kompass\.com|societe\.com|pappers\.fr|infogreffe\.fr|mappy\.com|justacote\.com|clutch\.co|sortlist\.[a-z.]+|goodfirms\.co|g2\.com|capterra\.[a-z.]+|appvizer\.[a-z.]+|crunchbase\.com|malt\.[a-z.]+|codeur\.com|fiverr\.com|upwork\.com|99designs\.[a-z.]+|lafabriquedunet\.fr|hellowork\.com|indeed\.[a-z.]+|houzz\.[a-z.]+|starofservice\.[a-z.]+)$/u, "directory"],
  [/(^|\.)(wix\.com|squarespace\.com|wordpress\.com|wordpress\.org|webflow\.com|shopify\.[a-z.]+|jimdo\.com|godaddy\.com|hostinger\.[a-z.]+|ionos\.[a-z.]+|site123\.com|weebly\.com|strikingly\.com|simplebo\.fr|e-monsite\.com|canva\.com|framer\.com|hubspot\.[a-z.]+|carrd\.co|zyro\.com|ovhcloud\.com|ovh\.com|odoo\.com|wpengine\.com|elementor\.com|durable\.co|10web\.io)$/u, "builder"],
  [/(\.gouv\.fr|\.gov|\.gov\.[a-z]+|\.gc\.ca|europa\.eu)$/u, "government"],
  [/(^|\.)(service-public\.fr|urssaf\.fr|bpifrance\.fr|bpifrance-creation\.fr|cci\.fr|cma-france\.fr|legifrance\.gouv\.fr|ameli\.fr|pole-emploi\.fr|francetravail\.fr)$/u, "government"],
  [/(^|\.)(facebook\.com|instagram\.com|linkedin\.com|x\.com|twitter\.com|tiktok\.com|pinterest\.[a-z.]+|youtube\.com|vimeo\.com)$/u, "social"],
  [/(^|\.)(wikipedia\.org|wiktionary\.org|larousse\.fr|lerobert\.com|britannica\.com)$/u, "reference"],
  [/(^|\.)(journaldunet\.com|journaldunet\.fr|lesechos\.fr|lefigaro\.fr|lemonde\.fr|01net\.com|bfmtv\.com|capital\.fr|challenges\.fr|forbes\.[a-z.]+|medium\.com|blogdumoderateur\.com|frenchweb\.fr|maddyness\.com|entrepreneur\.com|lecoindesentrepreneurs\.fr|legalstart\.fr|semrush\.com|ahrefs\.com|neilpatel\.com|abondance\.com)$/u, "media"],
];

/** What kind of site a search result is. Only `business` results are competitors. */
export function resultKind(host: string): ResultKind {
  const bare = host.toLowerCase().replace(/^www\./u, "");
  for (const [pattern, kind] of KIND_BY_HOST) if (pattern.test(bare)) return kind;
  return "business";
}

export interface RivalRow {
  position: number;
  title: string;
  url: string;
  host: string;
  /** The owner named this business as a competitor. */
  named: boolean;
}

export interface RivalSearch {
  query: string;
  capturedAt: string;
  /** Where the audited site was, or null when it was not in the captured results. */
  sitePosition: number | null;
  resultCount: number;
  businesses: RivalRow[];
  /** Results that are not businesses: forums, directories, builders, guides. */
  filtered: Array<{ host: string; kind: Exclude<ResultKind, "business"> }>;
  /** Where each owner-named competitor appeared in this search. */
  namedPositions: Array<{ competitor: OwnerNamedCompetitor; position: number | null }>;
}

export function rivalSearch(
  query: AssessmentBuyerQuery,
  ownDomain: string,
  named: readonly OwnerNamedCompetitor[],
): RivalSearch {
  const own = bareHost(ownDomain);
  const businesses: RivalRow[] = [];
  const filtered: RivalSearch["filtered"] = [];
  for (const result of query.results) {
    const host = bareHost(result.host || hostOf(result.url));
    if (sameSite(host, own)) continue;
    const kind = resultKind(host);
    if (kind !== "business") {
      filtered.push({ host, kind });
      continue;
    }
    businesses.push({
      position: result.position,
      title: result.title,
      url: result.url,
      host,
      named: named.some((competitor) => sameSite(host, competitor.domain)),
    });
  }
  return {
    query: query.query,
    capturedAt: query.capturedAt,
    sitePosition: query.sitePosition,
    resultCount: query.results.length,
    businesses,
    filtered,
    namedPositions: named.map((competitor) => ({
      competitor,
      position: query.results.find((result) => sameSite(bareHost(result.host || hostOf(result.url)), competitor.domain))?.position ?? null,
    })),
  };
}

/**
 * The search to open on: the first one the site is missing from that shows
 * real businesses, since that is the one a competitor wins.
 */
export function leadSearchIndex(queries: readonly AssessmentBuyerQuery[], ownDomain: string): number {
  const index = queries.findIndex((query) =>
    query.sitePosition === null && rivalSearch(query, ownDomain, []).businesses.length > 0);
  return index === -1 ? 0 : index;
}

// ── Questions customers ask ────────────────────────────────────────────────

export type QuestionsState =
  | { status: "not_checked" }
  | { status: "none_shown"; searches: number }
  | { status: "ready"; questions: AssessmentCustomerQuestion[]; answered: number };

export function customerQuestions(report: SiteAgainAssessmentReportV1): QuestionsState {
  const evidence = report.buyerEvidence;
  // Reports written before the questions were recorded carry no `homepage`
  // block either: they did not look, which is not the same as "none".
  if (!evidence || evidence.queries.length === 0 || evidence.homepage === undefined) return { status: "not_checked" };
  const questions = evidence.customerQuestions ?? [];
  if (questions.length === 0) return { status: "none_shown", searches: evidence.queries.length };
  return { status: "ready", questions, answered: questions.filter((item) => item.coverage.status === "covered").length };
}

// ── What is holding the site back ──────────────────────────────────────────

export type HoldingBackCheck =
  | { key: "speed"; status: CheckStatus; seconds: number | null; device: "mobile" | "desktop" | null }
  | { key: "readable"; status: CheckStatus }
  | { key: "title_length"; status: CheckStatus; title: string | null; length: number }
  | { key: "google_title"; status: CheckStatus; shown: string | null; actual: string | null }
  | { key: "descriptions"; status: CheckStatus; pagesMissing: number; pagesChecked: number };

export type CheckStatus = "pass" | "fail" | "not_checked";

/**
 * Five checks, each a consequence a customer feels. A check without evidence
 * is "not checked"; it never passes by default.
 */
export function holdingBackChecks(report: SiteAgainAssessmentReportV1): HoldingBackCheck[] {
  const evidence = report.buyerEvidence;
  const speed = evidence?.speed ?? null;
  const homepage = evidence?.homepage;
  const title = homepage?.title?.trim() || null;
  const brand = evidence?.brandQuery ?? null;
  const shown = brand?.siteUrl
    ? brand.results.find((result) => result.url === brand.siteUrl)?.title ?? null
    : null;
  const health = evidence?.pageHealth ?? null;
  const missingDescriptions = health?.issues.find((issue) => issue.key === "missing_meta_description")?.pagesAffected ?? 0;
  const missingTitles = health?.issues.find((issue) => issue.key === "missing_title")?.pagesAffected ?? 0;

  return [
    {
      key: "speed",
      status: speed === null ? "not_checked" : speed.loadSeconds <= SLOW_SECONDS ? "pass" : "fail",
      seconds: speed?.loadSeconds ?? null,
      device: speed?.device ?? (speed ? "desktop" : null),
    },
    {
      key: "readable",
      status: report.platform === undefined ? "not_checked" : report.platform.contentInHtml ? "pass" : "fail",
    },
    {
      key: "title_length",
      status: homepage === undefined ? "not_checked" : title !== null && title.length <= TITLE_CUTOFF ? "pass" : "fail",
      title,
      length: title?.length ?? 0,
    },
    {
      key: "google_title",
      status: shown === null || title === null ? "not_checked" : sameWords(shown, title) ? "pass" : "fail",
      shown,
      actual: title,
    },
    {
      key: "descriptions",
      status: health === null ? "not_checked" : Math.max(missingDescriptions, missingTitles) > 0 ? "fail" : "pass",
      pagesMissing: Math.max(missingDescriptions, missingTitles),
      pagesChecked: health?.pagesChecked ?? 0,
    },
  ];
}

/** Google often shortens a title or appends the brand; that is the same title, a different wording is not. */
function sameWords(shown: string, actual: string): boolean {
  const words = (text: string) => new Set(fold(text).split(/[^a-z0-9]+/u).filter((word) => word.length >= 3));
  const left = words(shown.replace(/\s*(\.\.\.|…)\s*$/u, ""));
  const right = words(actual);
  if (left.size === 0 || right.size === 0) return false;
  let shared = 0;
  for (const word of left) if (right.has(word)) shared += 1;
  return shared / left.size >= 0.6;
}

// ── Three actions ──────────────────────────────────────────────────────────

export type OwnerAction =
  | { key: "answer_questions"; impact: AssessmentSignalStrength; effort: AssessmentEffort; questions: AssessmentCustomerQuestion[]; total: number }
  | { key: "rank_for_search"; impact: AssessmentSignalStrength; effort: AssessmentEffort; query: string; rivals: string[] }
  | { key: "ai_answers"; impact: AssessmentSignalStrength; effort: AssessmentEffort; prompt: string; assistants: number }
  | { key: "fix_foundations"; impact: AssessmentSignalStrength; effort: AssessmentEffort; failing: HoldingBackCheck[] };

/** At most three, most useful first. An action exists only when evidence calls for it. */
export function ownerActions(report: SiteAgainAssessmentReportV1, ownDomain: string): OwnerAction[] {
  const evidence = report.buyerEvidence;
  const candidates: OwnerAction[] = [];

  const failing = holdingBackChecks(report).filter((check) => check.status === "fail");
  const foundations: OwnerAction | null = failing.length > 0
    ? { key: "fix_foundations", impact: failing.some((check) => check.key === "readable" || check.key === "speed") ? "high" : "medium", effort: "low", failing }
    : null;
  // A page Google cannot read undermines every other action, so it goes first.
  if (foundations && failing.some((check) => check.key === "readable")) candidates.push(foundations);

  const questions = customerQuestions(report);
  if (questions.status === "ready") {
    const open = questions.questions.filter((item) => item.coverage.status !== "covered");
    if (open.length > 0) {
      candidates.push({
        key: "answer_questions",
        impact: open.some((item) => item.impact === "high") ? "high" : "medium",
        effort: open.slice(0, 3).some((item) => item.effort !== "low") ? "medium" : "low",
        questions: open.slice(0, 3),
        total: open.length,
      });
    }
  }

  const missing = (evidence?.queries ?? []).find((query) => query.sitePosition === null);
  if (missing) {
    candidates.push({
      key: "rank_for_search",
      impact: "high",
      effort: "medium",
      query: missing.query,
      rivals: rivalSearch(missing, ownDomain, []).businesses.slice(0, 2).map((row) => row.host),
    });
  }

  const unmentioned = (evidence?.aiPrompts ?? []).find((prompt) =>
    prompt.answers.length > 0 && !prompt.answers.some((answer) => answer.siteMentioned));
  if (unmentioned) {
    candidates.push({ key: "ai_answers", impact: "medium", effort: "low", prompt: unmentioned.prompt, assistants: unmentioned.answers.length });
  }

  if (foundations && !candidates.includes(foundations)) candidates.push(foundations);
  return candidates.slice(0, 3);
}

// ── Technical details ──────────────────────────────────────────────────────

export interface TechnicalFact {
  key: string;
  value: number;
  unit: "seconds" | "ms" | "count" | "bytes" | "score";
}

/**
 * The developer's numbers. Estimated traffic is left out on purpose: it is a
 * model of everyone's clicks, and next to the owner's own site it reads as theirs.
 */
export function technicalFacts(report: SiteAgainAssessmentReportV1): TechnicalFact[] {
  const evidence = report.buyerEvidence;
  const facts: TechnicalFact[] = [];
  const push = (key: string, value: number | null | undefined, unit: TechnicalFact["unit"]) => {
    if (typeof value === "number" && Number.isFinite(value)) facts.push({ key, value, unit });
  };
  push("load", evidence?.speed?.loadSeconds, "seconds");
  push("ttfb", evidence?.speed?.ttfbMs, "ms");
  push("requests", evidence?.speed?.requests, "count");
  push("transfer", evidence?.speed?.transferBytes, "bytes");
  push("authority", evidence?.backlinkSummary?.domainAuthority, "score");
  push("referring", evidence?.backlinkSummary?.referringDomains, "count");
  push("backlinks", evidence?.backlinkSummary?.totalBacklinks, "count");
  push("rankedKeywords", evidence?.domainKeywords?.totalRankedKeywords, "count");
  push("top10Keywords", evidence?.domainKeywords?.top10Keywords, "count");
  push("strikingKeywords", evidence?.domainKeywords?.strikingDistanceKeywords, "count");
  push("pagesChecked", evidence?.pageHealth?.pagesChecked, "count");
  for (const key of ["seo", "geo", "performance", "maintainability"] as const) {
    const dimension = report.dimensions[key];
    if (dimension.confidence !== "insufficient") push(`dimension.${key}`, dimension.score, "score");
  }
  return facts;
}

// ── helpers ────────────────────────────────────────────────────────────────

export function bareHost(host: string): string {
  return host.trim().toLowerCase().replace(/^www\./u, "");
}

function sameSite(host: string, domain: string): boolean {
  const left = bareHost(host);
  const right = bareHost(domain);
  return Boolean(left && right) && (left === right || left.endsWith(`.${right}`));
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

function fold(text: string): string {
  return text.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/gu, "");
}
